import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Building2, Shield, Bell, Database, Globe2, Key,
  ChevronRight, Check, AlertTriangle, Mail, MessageSquare,
  Smartphone, Palette, Lock, Eye, EyeOff, ExternalLink
} from 'lucide-react';
import { mockUsers } from '../../data/mockData';
import { getInitials } from '../../utils/helpers';

export default function SettingsPage({ section = 'company' }: { section?: string }) {
  const [activeTab, setActiveTab] = useState(section);

  const tabs = [
    { id: 'company', label: 'Company Settings', icon: Building2 },
    { id: 'users', label: 'Users & Roles', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Globe2 },
    { id: 'landing', label: 'Landing Page', icon: ExternalLink },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Configure your platform preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-slate-100 p-2 space-y-1">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id ? 'bg-sky-50 text-sky-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}>
                <tab.icon className="w-5 h-5" />
                <span className="flex-1 text-left">{tab.label}</span>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-4">
          {activeTab === 'company' && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-slate-800">Company Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Company Name</label>
                  <input type="text" defaultValue="MaritiMEX Shipping" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Industry</label>
                  <select className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                    <option>Shipping & Logistics</option>
                    <option>Maritime</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Email</label>
                  <input type="email" defaultValue="admin@maritimex.com" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Phone</label>
                  <input type="tel" defaultValue="+65 9123 4567" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Address</label>
                  <textarea defaultValue="123 Maritime Lane, Singapore 098765" rows={2} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none" />
                </div>
              </div>
              <div className="flex justify-end">
                <button className="px-6 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors">Save Changes</button>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-800">Team Members</h2>
                  <button className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors">Invite User</button>
                </div>
                <div className="space-y-3">
                  {mockUsers.map(user => (
                    <div key={user.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                      <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {getInitials(user.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{user.name}</p>
                        <p className="text-xs text-slate-400">{user.email}</p>
                      </div>
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full capitalize">
                        {user.role.replace('_', ' ')}
                      </span>
                      <span className={`w-2.5 h-2.5 rounded-full ${user.isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-slate-800">Notification Preferences</h2>
              {[
                { label: 'High-Risk Findings', desc: 'Get notified when critical findings are recorded', icon: AlertTriangle, enabled: true },
                { label: 'Overdue Corrective Actions', desc: 'Alert when CAPAs pass their due date', icon: Clock, enabled: true },
                { label: 'Report Approvals', desc: 'Notification when reports need your approval', icon: Check, enabled: true },
                { label: 'AI Risk Predictions', desc: 'Alert when AI predicts inspection failure', icon: Shield, enabled: true },
                { label: 'Upcoming Inspections', desc: 'Reminder 7 days before scheduled inspection', icon: Bell, enabled: false },
              ].map(pref => (
                <div key={pref.label} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                    <pref.icon className="w-5 h-5 text-sky-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{pref.label}</p>
                    <p className="text-xs text-slate-400">{pref.desc}</p>
                  </div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Smartphone className="w-3.5 h-3.5" /> Push
                    </label>
                  </div>
                  <div className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors ${pref.enabled ? 'bg-sky-500' : 'bg-slate-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${pref.enabled ? 'left-5.5' : 'left-0.5'}`} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-4">Security Settings</h2>
                <div className="space-y-4">
                  {[
                    { label: 'Two-Factor Authentication', desc: 'Add an extra layer of security to your account', enabled: true },
                    { label: 'SSO Integration', desc: 'Enable Single Sign-On with your identity provider', enabled: true },
                    { label: 'Session Timeout', desc: 'Automatically log out after 30 minutes of inactivity', enabled: true },
                    { label: 'IP Whitelist', desc: 'Restrict access to specific IP addresses', enabled: false },
                  ].map(setting => (
                    <div key={setting.label} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                      <Lock className="w-5 h-5 text-slate-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{setting.label}</p>
                        <p className="text-xs text-slate-400">{setting.desc}</p>
                      </div>
                      <div className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors ${setting.enabled ? 'bg-sky-500' : 'bg-slate-300'}`}>
                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${setting.enabled ? 'left-5.5' : 'left-0.5'}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-4">API Keys</h2>
                <div className="space-y-3">
                  {[
                    { name: 'Production API Key', key: 'sk_prod_•••••••••••••••••', created: '2026-01-15', active: true },
                    { name: 'Staging API Key', key: 'sk_stg_•••••••••••••••••', created: '2026-03-10', active: true },
                  ].map(apiKey => (
                    <div key={apiKey.name} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                      <Key className="w-5 h-5 text-slate-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{apiKey.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{apiKey.key}</p>
                      </div>
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Integrations</h2>
              <div className="space-y-3">
                {[
                  { name: 'RightShip', desc: 'Connect to RightShip for vessel data sync', status: 'Connected', color: 'text-emerald-600' },
                  { name: 'Microsoft Azure AD', desc: 'Single Sign-On via Azure Active Directory', status: 'Connected', color: 'text-emerald-600' },
                  { name: 'Slack', desc: 'Send notifications to Slack channels', status: 'Not configured', color: 'text-slate-400' },
                  { name: 'Twilio', desc: 'SMS notifications for critical alerts', status: 'Not configured', color: 'text-slate-400' },
                ].map(integration => (
                  <div key={integration.name} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                      <Globe2 className="w-5 h-5 text-sky-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">{integration.name}</p>
                      <p className="text-xs text-slate-400">{integration.desc}</p>
                    </div>
                    <span className={`text-xs font-medium ${integration.color}`}>{integration.status}</span>
                    <button className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                      Configure
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'landing' && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-slate-800">Landing Page</h2>
              <p className="text-sm text-slate-500">Access and preview your public landing page.</p>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm font-medium text-slate-700 mb-1">Public URL</p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 font-mono">/landing</code>
                  <Link
                    to="/landing"
                    target="_blank"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors"
                  >
                    Visit Landing Page
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
